/**MTNIntelligence.js — Climate & Circularity Intelligence */
import React, { useState } from "react";
import { useMTN } from "@/context/MTNContext";
import { AlertTriangle, TrendingDown } from "lucide-react";
import ExecutiveActionPanel from "@/components/ExecutiveActionPanel";

const fmtN=(v,u="")=>{ if(!v&&v!==0)return"—"; const n=Number(v); if(n>=1e6)return`${(n/1e6).toFixed(1)}M ${u}`.trim(); if(n>=1e3)return`${(n/1e3).toFixed(0)}K ${u}`.trim(); return`${n.toLocaleString()} ${u}`.trim(); };
const fmtU=v=>{if(!v&&v!==0)return"—";const n=Number(v);if(n>=1e9)return`$${(n/1e9).toFixed(1)}B`;if(n>=1e6)return`$${(n/1e6).toFixed(1)}M`;if(n>=1e3)return`$${(n/1e3).toFixed(0)}K`;return`$${n}`;};
const TABS=[{id:"emissions",label:"Emissions"},{id:"reduction",label:"Reduction"},{id:"intensity",label:"Intensity"},{id:"value",label:"Value at Risk"},{id:"circularity",label:"Circularity"},{id:"ops",label:"Operational"}];

export default function MTNIntelligence() {
  const { group, businessUnits, loading, selectedPeriod } = useMTN();
  const [activeTab,setActiveTab]=useState("emissions");
  if(loading)return<div style={{padding:40,color:"#64748b",fontSize:12}}>Loading…</div>;
  const g=group;
  const towerBUs=businessUnits.filter(b=>b.sector==="tower_operations");

  return(
    <div style={{padding:"24px 28px"}}>
      <div style={{marginBottom:20}}>
        <span style={{fontSize:9,fontWeight:800,color:"#FFCB00",letterSpacing:".08em",textTransform:"uppercase",background:"#FFCB0015",border:"1px solid #FFCB0030",padding:"2px 8px",borderRadius:4}}>MTN Nigeria</span>
        <h1 style={{fontSize:20,fontWeight:900,color:"#f1f5f9",marginTop:6}}>Climate & Circularity Intelligence</h1>
        <p style={{fontSize:11,color:"#475569"}}>Network-level emissions analytics · Tower intensity · Diesel hotspots · Circularity metrics</p>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:4,background:"#0a0f1e",border:"1px solid #1e293b",borderRadius:8,padding:6,marginBottom:16}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setActiveTab(t.id)}
            style={{flex:1,padding:"7px 8px",borderRadius:6,border:"none",cursor:"pointer",fontSize:10,fontWeight:600,
              background:activeTab===t.id?"#FFCB00":"transparent",color:activeTab===t.id?"#000":"#475569"}}
            data-testid={`intel-tab-${t.id}`}>{t.label}
          </button>
        ))}
      </div>

      {/* EMISSIONS */}
      {activeTab==="emissions" && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}} data-testid="emissions-waterfall">
            <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:14}}>Emissions Waterfall — by Business Unit</p>
            {[...businessUnits].sort((a,b)=>b.total_tco2e-a.total_tco2e).map(bu=>{
              const pct=Math.min(100,(bu.total_tco2e/Math.max(g.total_tco2e,1))*100);
              return(
                <div key={bu.bu_id} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:9,color:"#94a3b8"}}>{bu.name?.substring(0,30)}</span>
                    <span style={{fontSize:9,fontWeight:700,color:"#f1f5f9"}}>{fmtN(bu.total_tco2e,"tCO₂e")} ({pct.toFixed(0)}%)</span>
                  </div>
                  <div style={{height:6,background:"#1e293b",borderRadius:3,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pct}%`,background:bu.sector==="tower_operations"?"#f59e0b":bu.sector==="data_center"?"#60a5fa":"#8b5cf6",borderRadius:3}}/>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
            <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:14}}>Scope 1 Breakdown — Key Sources</p>
            {[["Diesel Generators",businessUnits.reduce((s,b)=>s+(b.scope1?.diesel||0),0),"#ef4444"],
              ["Natural Gas",businessUnits.reduce((s,b)=>s+(b.scope1?.gas||0),0),"#f59e0b"],
              ["Fleet Operations",businessUnits.reduce((s,b)=>s+(b.scope1?.fleet||0),0),"#f97316"],
              ["Process (Refrigerants)",businessUnits.reduce((s,b)=>s+(b.scope1?.process||0),0),"#ec4899"],
            ].map(([lbl,val,col])=>{
              const pct=Math.min(100,(val/Math.max(g.scope1_tco2e,1))*100);
              return(
                <div key={lbl} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:9,color:"#94a3b8"}}>{lbl}</span>
                    <span style={{fontSize:9,fontWeight:700,color:col}}>{fmtN(val,"tCO₂e")} ({pct.toFixed(0)}%)</span>
                  </div>
                  <div style={{height:5,background:"#1e293b",borderRadius:2,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pct}%`,background:col,borderRadius:2}}/>
                  </div>
                </div>
              );
            })}
            <div style={{background:"#111827",borderRadius:6,padding:10,marginTop:10}}>
              <p style={{fontSize:8,color:"#64748b"}}>Diesel generators are the dominant Scope 1 source. Nigeria grid instability requires 8-16 hours/day generator runtime across most tower clusters.</p>
            </div>
          </div>
        </div>
      )}

      {/* REDUCTION */}
      {activeTab==="reduction" && (
        <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}} data-testid="reduction-bridge">
          <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:14}}>Reduction Bridge — All Levers ({fmtN(g.reduction_potential_tco2e,"tCO₂e/yr")} total potential)</p>
          {[["Tower Solarisation",g.reduction_breakdown?.tower_solarisation,"#FFCB00","Primary — AMS-I.D PoA. 5,000 sites Phase 1."],
            ["Battery Optimisation",g.reduction_breakdown?.battery_optimisation,"#60a5fa","Smart battery mgmt. Reduce generator runtime."],
            ["RE Procurement",g.reduction_breakdown?.re_procurement,"#22c55e","I-RECs + SLL alignment. Immediate impact."],
            ["Fleet Transition",g.reduction_breakdown?.fleet_transition,"#f97316","CNG dual-fuel. 800 vehicles Phase 1."],
            ["DC Cooling",g.reduction_breakdown?.dc_cooling,"#a78bfa","PUE improvement 2.0→1.5. 5 DCs."],
          ].map(([lbl,val,col,note])=>{
            const pct=Math.min(100,(val/Math.max(g.reduction_potential_tco2e,1))*100);
            return(
              <div key={lbl} style={{marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                  <span style={{fontSize:10,fontWeight:600,color:"#f1f5f9"}}>{lbl}</span>
                  <span style={{fontSize:10,fontWeight:700,color:col}}>{fmtN(val,"tCO₂e/yr")}</span>
                </div>
                <div style={{height:8,background:"#1e293b",borderRadius:4,overflow:"hidden",marginBottom:4}}>
                  <div style={{height:"100%",width:`${pct}%`,background:col+"30",borderLeft:`3px solid ${col}`,borderRadius:4}}/>
                </div>
                <p style={{fontSize:8,color:"#475569"}}>{note}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* INTENSITY */}
      {activeTab==="intensity" && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}} data-testid="intensity-analysis">
          <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
            <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:14}}>Tower Emission Intensity (kg CO₂e/tower)</p>
            {towerBUs.sort((a,b)=>(b.tower_intensity_kg||0)-(a.tower_intensity_kg||0)).map(bu=>{
              const pct=Math.min(100,((bu.tower_intensity_kg||0)/Math.max(...towerBUs.map(b=>b.tower_intensity_kg||0),1))*100);
              return(
                <div key={bu.bu_id} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:9,color:"#94a3b8"}}>{bu.name?.replace("Tower Infrastructure — ","")}</span>
                    <span style={{fontSize:9,fontWeight:700,color:(bu.tower_intensity_kg||0)>25000?"#ef4444":"#f59e0b"}}>{fmtN(bu.tower_intensity_kg||0,"kg")}</span>
                  </div>
                  <div style={{height:5,background:"#1e293b",borderRadius:2,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pct}%`,background:(bu.tower_intensity_kg||0)>25000?"#ef4444":"#f59e0b",borderRadius:2}}/>
                  </div>
                  <div style={{display:"flex",gap:10,marginTop:2}}>
                    <span style={{fontSize:8,color:"#475569"}}>Solar: {(bu.renewable_pct||0).toFixed(0)}%</span>
                    <span style={{fontSize:8,color:"#475569"}}>Diesel: {(bu.diesel_dependency||0).toFixed(0)}%</span>
                    <span style={{fontSize:8,color:"#475569"}}>{(bu.tower_count||0).toLocaleString()} sites</span>
                  </div>
                </div>
              );
            })}
            <div style={{background:"#111827",borderRadius:6,padding:10,marginTop:10}}>
              <p style={{fontSize:8,color:"#64748b"}}>Group average: {fmtN(g.group_tower_intensity_kg||0,"kg CO₂e/tower")}. GSMA benchmark: ~5,000 kg for hybrid markets.</p>
            </div>
          </div>
          <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
            <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:14}}>Energy Mix by Cluster</p>
            {towerBUs.map(bu=>(
              <div key={bu.bu_id} style={{marginBottom:12,borderBottom:"1px solid #0f172a",paddingBottom:10}}>
                <p style={{fontSize:9,fontWeight:700,color:"#f1f5f9",marginBottom:5}}>{bu.name?.replace("Tower Infrastructure — ","")}</p>
                <div style={{display:"flex",gap:2,height:10,borderRadius:5,overflow:"hidden",marginBottom:4}}>
                  <div style={{width:`${bu.diesel_dependency||0}%`,background:"#ef4444",minWidth:2}}/>
                  <div style={{width:`${Math.max(0,100-(bu.diesel_dependency||0)-(bu.renewable_pct||0))}%`,background:"#60a5fa",minWidth:2}}/>
                  <div style={{width:`${bu.renewable_pct||0}%`,background:"#22c55e",minWidth:2}}/>
                </div>
                <div style={{display:"flex",gap:8,fontSize:8,color:"#64748b"}}>
                  <span style={{color:"#ef4444"}}>Diesel {(bu.diesel_dependency||0).toFixed(0)}%</span>
                  <span style={{color:"#60a5fa"}}>Grid {Math.max(0,100-(bu.diesel_dependency||0)-(bu.renewable_pct||0)).toFixed(0)}%</span>
                  <span style={{color:"#22c55e"}}>Solar {(bu.renewable_pct||0).toFixed(0)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CIRCULARITY */}
      {activeTab==="circularity" && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}} data-testid="circularity-analysis">
          <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
            <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:14}}>Circularity Scores by Business Unit</p>
            {businessUnits.map(bu=>{
              const s=bu.circularity_score||0;
              return(
                <div key={bu.bu_id} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:9,color:"#94a3b8"}}>{bu.name?.substring(0,28)}</span>
                    <span style={{fontSize:9,fontWeight:700,color:s>=60?"#22c55e":s>=40?"#FFCB00":"#ef4444"}}>{s.toFixed(1)}/100</span>
                  </div>
                  <div style={{height:5,background:"#1e293b",borderRadius:2,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${s}%`,background:s>=60?"#22c55e":s>=40?"#FFCB00":"#ef4444",borderRadius:2}}/>
                  </div>
                </div>
              );
            })}
            <p style={{fontSize:8,color:"#475569",marginTop:10}}>Group avg: {(g.circularity_score_avg||0).toFixed(1)}/100. Target: 65+</p>
          </div>
          <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
            <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:14}}>Circularity Programmes</p>
            {[["E-waste Recovery", fmtN(businessUnits.reduce((s,b)=>s+(b.ewaste_recovered_t||0),0),"t/yr"), "Annual recovery rate"],
              ["SIM Recycling", fmtN(businessUnits.reduce((s,b)=>s+(b.sim_recycled_count||0),0),"units/yr"), "Programme active"],
              ["Battery Lifecycle","Active programme","Replacement + recovery"],
              ["Equipment Reuse",`${(businessUnits.reduce((s,b)=>s+(b.equipment_reused_pct||0),0)/Math.max(businessUnits.length,1)).toFixed(0)}% avg`,"Refurbishment active"],
            ].map(([k,v,s])=>(
              <div key={k} style={{marginBottom:10,borderBottom:"1px solid #0f172a",paddingBottom:8}}>
                <p style={{fontSize:9,fontWeight:600,color:"#f1f5f9"}}>{k}</p>
                <p style={{fontSize:10,fontWeight:700,color:"#22c55e"}}>{v}</p>
                <p style={{fontSize:8,color:"#475569"}}>{s}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* VALUE AT RISK */}
      {activeTab==="value" && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}} data-testid="value-at-risk">
          <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
            <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:14}}>Top 5 Financial Leakage Sources</p>
            {[
              ["Tower Diesel Spend", Math.round(139e6*0.75/1e6)+"M USD/yr", "#ef4444", "Primary Scope 1 cost — grid instability premium"],
              ["Grid Electricity Cost", `$${Math.round((g.scope2_tco2e||0)*1000/0.43*0.04/1e6)}M USD/yr`, "#f59e0b", "Grid load optimisation opportunity"],
              ["Generator Maintenance", "~$8M USD/yr", "#f97316", "Service + parts for 18,500+ generators"],
              ["Fuel Theft & Leakage", "~$12M USD/yr", "#ef4444", "Estimated 5-8% fuel loss across distributed fleet"],
              ["Battery Degradation", "~$4M USD/yr", "#a78bfa", "Premature replacement from poor charge management"],
            ].map(([l,v,c,n])=>(
              <div key={l} style={{marginBottom:10,borderBottom:"1px solid #0f172a",paddingBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                  <span style={{fontSize:9,fontWeight:700,color:"#f1f5f9"}}>{l}</span>
                  <span style={{fontSize:10,fontWeight:900,color:c}}>{v}</span>
                </div>
                <p style={{fontSize:8,color:"#475569"}}>{n}</p>
              </div>
            ))}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
              <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>Top 5 Immediate Reduction Opportunities</p>
              {[
                ["Tower Solarisation Phase 1", "$" + Math.round((g.reduction_breakdown?.tower_solarisation||0)*15/1e6)+"M savings/yr", "#22c55e","18mo timeline · PoA structure · AMS-I.D"],
                ["Battery Optimisation", "$" + Math.round((g.reduction_breakdown?.battery_optimisation||0)*15/1e6)+"M savings/yr","#60a5fa","Immediate · 10mo · No methodology risk"],
                ["RE Procurement (I-RECs)","Scope 2 displacement","#a78bfa","Fastest time-to-impact · SLL alignment"],
                ["Smart Metering Rollout","Unlocks VVB pathway","#FFCB00","Evidence upgrade · 6mo · Top 500 sites"],
                ["Fleet CNG Transition","$" + Math.round((g.reduction_breakdown?.fleet_transition||0)*15/1e6)+"M savings/yr","#f97316","16mo · 800 vehicles Phase 1"],
              ].map(([l,v,c,n])=>(
                <div key={l} style={{marginBottom:8,borderBottom:"1px solid #0f172a",paddingBottom:6}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:1}}>
                    <span style={{fontSize:9,fontWeight:700,color:"#f1f5f9"}}>{l}</span>
                    <span style={{fontSize:9,fontWeight:700,color:c}}>{v}</span>
                  </div>
                  <p style={{fontSize:8,color:"#475569"}}>{n}</p>
                </div>
              ))}
            </div>
            <div style={{background:"#7f1d1d15",border:"1px solid #ef444425",borderRadius:8,padding:12}}>
              <p style={{fontSize:9,fontWeight:700,color:"#ef4444",marginBottom:4}}>Carbon Intensity Cost Exposure</p>
              <p style={{fontSize:10,fontWeight:900,color:"#f87171"}}>{fmtN((g.total_tco2e||0)*15,"USD")} indicative annual exposure</p>
              <p style={{fontSize:9,color:"#94a3b8",marginTop:3}}>At $15/tCO₂e conservative carbon price. At $50/tCO₂e (EU ETS proxy): {fmtU((g.total_tco2e||0)*50)}/yr. Carbon pricing trajectory increases this exposure annually.</p>
            </div>
          </div>
        </div>
      )}

      {/* OPERATIONAL */}
      {activeTab==="ops" && (
        <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}} data-testid="operational-insights">
          <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:14}}>Operational Leverage Analysis</p>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
            {[["18,500+","Active Tower Sites","Across 4 geographic clusters"],
              [`${(g.group_diesel_dep||0).toFixed(0)}%`,"Diesel Dependency","Tower energy mix — primary risk"],
              [`${(g.group_renewable_pct||0).toFixed(0)}%`,"Renewable Penetration","Solar hybrid + RE procurement"],
              [`${fmtN(g.total_towers||0)}`,"Towers Tracked","SSOT-verified inventory"],
              [`${(g.data_confidence_avg||0).toFixed(0)}/100`,"Data Confidence","Evidence quality score"],
              [`${fmtN(g.group_tower_intensity_kg||0,"kg")}`,"Avg Tower Intensity","kg CO₂e per tower site"],
            ].map(([v,l,s])=>(
              <div key={l} style={{background:"#060c1a",borderRadius:6,padding:12,border:"1px solid #1e293b"}}>
                <p style={{fontSize:16,fontWeight:900,color:"#FFCB00"}}>{v}</p>
                <p style={{fontSize:9,fontWeight:600,color:"#94a3b8"}}>{l}</p>
                <p style={{fontSize:8,color:"#475569"}}>{s}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
